import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  DialogContentText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { deleteAiSummaryRecording } from "../../connections/deleteApi";
import { showToast } from "../../utils/toastUtil";

const VoiceControlModal = ({
  open,
  onClose,
  onRecordNew,
  voiceSummary,
  patientName,
  patientDetailsData,
  appointmentData,
  refetch,
}) => {
  const fileUri = voiceSummary?.payload?.audio_url?.url;
  const recordedDate = voiceSummary?.payload?.created_at;

  const [openConfirm, setOpenConfirm] = useState(false);

  const executeDelete = async () => {
    console.log("Delete recording for:", patientName);
    // Future: implement delete API call
    try {
      const data = await deleteAiSummaryRecording(voiceSummary?.payload?.id);
      if (data.status?.status === "SUCCESS") {
        refetch();
        setOpenConfirm(false);
        onClose();
      } else {
        showToast(
          data?.status?.message || data?.status?.status,
          data?.status?.status
        );
      }
    } catch (error) {
      toast.error(error?.status?.message || "something went wrong");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", overflow: "hidden" },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            backgroundColor: "#055f75ff", // Green background
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PlayCircleFilledIcon sx={{ fontSize: 32 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: "Lato", fontWeight: 700, fontSize: "20px" }}
            >
              Voice Recording Controls
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Lato",
              color: "#6b7280",
              mb: 3,
              fontSize: "16px",
              mt: 3,
            }}
          >
            Recorded on {recordedDate}
          </Typography>

          {/* Audio Player */}
          <Box sx={{ mb: 4, width: "100%" }}>
            {fileUri ? (
              <audio controls style={{ width: "100%" }}>
                <source src={fileUri} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            ) : (
              <Typography sx={{ color: "#ef4444", fontFamily: "Lato" }}>
                Audio file not found
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenConfirm(true)}
              startIcon={<DeleteOutlineIcon />}
              sx={{
                borderColor: "#ef4444",
                color: "#ef4444",
                textTransform: "none",
                fontFamily: "Lato",
                fontWeight: 600,
                px: 3,
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "#dc2626",
                  backgroundColor: "#fef2f2",
                },
              }}
            >
              Delete Recording
            </Button>
            <Button
              variant="outlined"
              onClick={onRecordNew}
              disabled={
                !patientDetailsData?.id || appointmentData?.status !== "paid"
              }
              startIcon={<AddIcon />}
              sx={{
                borderColor: "#374151",
                color: "#374151",
                textTransform: "none",
                fontFamily: "Lato",
                fontWeight: 600,
                px: 3,
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "#111827",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Record New
            </Button>
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            backgroundColor: "#f9fafb",
            px: 3,
            py: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon sx={{ fontSize: "18px !important" }} />}
            sx={{
              borderColor: "#374151",
              color: "#374151",
              textTransform: "none",
              fontFamily: "Lato",
              fontWeight: 600,
              px: 3,
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "white",
              "&:hover": {
                borderColor: "#111827",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { borderRadius: "12px", p: 1 },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            fontFamily: "Lato",
            fontWeight: 700,
            color: "#111827",
          }}
        >
          {"Delete Recording?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              fontFamily: "Lato",
              color: "#4b5563",
              fontSize: "16px",
            }}
          >
            Are you sure you want to delete this recording? This will also delete
            the AI summary related to that recording.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenConfirm(false)}
            variant="outlined"
            sx={{
              borderColor: "#374151",
              color: "#374151",
              textTransform: "none",
              fontFamily: "Lato",
              fontWeight: 600,
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#111827",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeDelete}
            variant="contained"
            color="error"
            autoFocus
            sx={{
              backgroundColor: "#ef4444",
              textTransform: "none",
              fontFamily: "Lato",
              fontWeight: 600,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceControlModal;
